import { Phone, MessageCircle, Instagram, Facebook, Music2, Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { RESTAURANT, waLink } from "@/data/restaurant";
import { useOrderCapture } from "@/components/OrderCapture";

export default function ContactSection() {
  const [copied, setCopied] = useState(false);
  const { requestOrder } = useOrderCapture();

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(RESTAURANT.phoneDisplay);
      setCopied(true);
      toast.success("Number copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please dial manually");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 sm:py-28 bg-[#FFFCF5] border-t border-[#E8E2D6]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="reveal max-w-2xl">
          <p className="text-[#D9534F] text-xs font-semibold tracking-[0.25em] uppercase">
            Talk to Meritz
          </p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            One ring, one message, one DM away.
          </h2>
          <p className="mt-4 text-[#2C2825]/75 leading-relaxed">
            Catering, private events, partnerships, or just to say the jollof
            was unreal — we&rsquo;d love to hear from you.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Phone (primary, big) */}
          <div
            data-testid="contact-card-phone"
            className="sm:col-span-2 reveal bg-gradient-to-br from-[#C73E3A] to-[#D9534F] text-white rounded-3xl p-7 flex flex-col justify-between shadow-lg shadow-[#C73E3A]/15 hover:-translate-y-0.5 transition-transform"
          >
            <div className="flex items-start justify-between">
              <span className="w-12 h-12 rounded-full bg-white/15 grid place-items-center">
                <Phone size={22} />
              </span>
              <button
                onClick={copyPhone}
                data-testid="copy-phone-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-full transition-colors"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="mt-8">
              <p className="text-white/70 text-xs uppercase tracking-[0.2em]">
                Call us
              </p>
              <a
                href={`tel:${RESTAURANT.phoneRaw}`}
                data-testid="contact-phone"
                className="block mt-1 font-display text-3xl sm:text-4xl font-extrabold tracking-tight hover:underline"
              >
                {RESTAURANT.phoneDisplay}
              </a>
              <p className="mt-2 text-sm text-white/80">
                Daily, 10am – 11pm. We answer fast.
              </p>
            </div>
          </div>

          {/* WhatsApp */}
          <button
            type="button"
            onClick={() => requestOrder(waLink("Hello Meritz! I'd like to enquire about your menu / event."))}
            data-testid="contact-card-whatsapp"
            className="reveal text-left bg-[#25D366] text-white rounded-3xl p-6 flex flex-col justify-between min-h-[200px] hover:-translate-y-0.5 transition-transform"
          >
            <span className="w-12 h-12 rounded-full bg-white/15 grid place-items-center">
              <MessageCircle size={22} />
            </span>
            <div>
              <p className="text-white/80 text-xs uppercase tracking-[0.2em]">
                WhatsApp
              </p>
              <p className="mt-1 font-display text-xl font-extrabold leading-tight">
                Chat with us now
              </p>
              <p className="mt-2 text-sm text-white/85">Tap to start chatting →</p>
            </div>
          </button>

          {/* TikTok */}
          <a
            href={RESTAURANT.socials.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-card-tiktok"
            className="reveal relative overflow-hidden bg-black text-white rounded-3xl p-6 flex flex-col justify-between min-h-[200px] hover:-translate-y-0.5 transition-transform"
          >
            <span
              aria-hidden
              className="absolute inset-0 opacity-25"
              style={{
                background:
                  "radial-gradient(120% 60% at 20% 20%, #25F4EE 0%, transparent 50%), radial-gradient(120% 60% at 80% 80%, #FE2C55 0%, transparent 55%)",
              }}
            />
            <span className="relative w-12 h-12 rounded-full bg-white/15 grid place-items-center">
              <Music2 size={22} />
            </span>
            <div className="relative">
              <p className="text-white/80 text-xs uppercase tracking-[0.2em]">
                TikTok
              </p>
              <p className="mt-1 font-display text-xl font-extrabold leading-tight">
                {RESTAURANT.socials.tiktokHandle}
              </p>
              <p className="mt-2 text-sm text-white/80">
                Watch the kitchen in action →
              </p>
            </div>
          </a>

          {/* Instagram */}
          <a
            href={RESTAURANT.socials.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-card-instagram"
            className="reveal sm:col-span-2 lg:col-span-2 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white rounded-3xl p-6 flex items-center justify-between min-h-[200px] hover:-translate-y-0.5 transition-transform"
          >
            <div>
              <p className="text-white/80 text-xs uppercase tracking-[0.2em]">
                Instagram
              </p>
              <p className="mt-1 font-display text-xl font-extrabold leading-tight">
                {RESTAURANT.socials.instagramHandle}
              </p>
              <p className="mt-2 text-sm text-white/85">
                See today&rsquo;s plates →
              </p>
            </div>
            <span className="w-14 h-14 rounded-full bg-white/15 grid place-items-center">
              <Instagram size={26} />
            </span>
          </a>

          {/* Facebook */}
          <a
            href={RESTAURANT.socials.facebook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-card-facebook"
            className="reveal bg-[#1877F2] text-white rounded-3xl p-6 flex flex-col justify-between min-h-[200px] hover:-translate-y-0.5 transition-transform"
          >
            <span className="w-12 h-12 rounded-full bg-white/15 grid place-items-center">
              <Facebook size={22} />
            </span>
            <div>
              <p className="text-white/80 text-xs uppercase tracking-[0.2em]">
                Facebook
              </p>
              <p className="mt-1 font-display text-xl font-extrabold leading-tight">
                {RESTAURANT.socials.facebookHandle}
              </p>
              <p className="mt-2 text-sm text-white/85">
                Events &amp; weekly specials →
              </p>
            </div>
          </a>

          {/* Walk-in card */}
          <div
            data-testid="contact-card-walkin"
            className="reveal bg-[#2C2825] text-white rounded-3xl p-6 flex flex-col justify-between min-h-[200px]"
          >
            <p className="text-[#F2B441] text-xs uppercase tracking-[0.25em] font-semibold">
              Walk-in welcome
            </p>
            <div>
              <p className="font-display text-lg font-extrabold leading-tight">
                {RESTAURANT.address}
              </p>
              <a
                href={RESTAURANT.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-walkin-directions"
                className="mt-3 inline-flex text-sm font-semibold text-[#F2B441] hover:underline"
              >
                Get directions →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
